import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import jwt_decode from "jwt-decode";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { captureException } from "@sentry/react";
import { pushToDataLayer } from "@/data-layer/push-to-data-layer";
import { useOnQueryError } from "@/hooks";
import { HydraErrorResponse } from "@/types";
import StorageService from "@/services";
import queryClient from "@/lib/query-client";
import { Account } from "@/features/accounts";
import { useGetAccountDetails, useLogout } from "../hooks";
import { AuthenticatedUser, Credentials, LoginSuccessResponse, TokenKey, LogoutParams } from "../types";
import { AuthService } from "../services";

const AuthContext = createContext<{
  accountDetails: Account | undefined;
  login: UseMutateFunction<LoginSuccessResponse, AxiosError<HydraErrorResponse>, Credentials>;
  logout: (params?: LogoutParams) => void;
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  isLogoutPending: boolean;
  reloadUser: () => void;
} | null>(null);

const AuthProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation("coi-platform-translations", {
    keyPrefix: "auth",
  });
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const onError = useOnQueryError("error.backend.default_error");

  const { mutateAsync: appLogout, isPending: isLogoutPending } = useLogout();

  const { enqueueSnackbar } = useSnackbar();

  const { data: accountDetails } = useGetAccountDetails(user?.account_id || null);

  const reloadUser = useCallback(() => {
    const token = StorageService.getItem(TokenKey.TOKEN);
    try {
      const userDecoded = jwt_decode<AuthenticatedUser | null>(token || "");
      setUser(userDecoded);
    } catch (error) {
      captureException(error);
      setUser(null);
    }
  }, []);

  const removeUser = useCallback(() => {
    StorageService.removeItem(TokenKey.TOKEN);
    reloadUser();
  }, [reloadUser]);

  const { isPending, mutate } = useMutation<LoginSuccessResponse, AxiosError<HydraErrorResponse>, Credentials>({
    mutationKey: ["login"],
    mutationFn: AuthService.login,
    onSuccess: () => {
      reloadUser();
    },
    onError,
  });

  const setLogoutNotification = useCallback(
    (unauthorized = true) => {
      enqueueSnackbar(t(unauthorized ? "notification_unauthorized_error" : "notification_logout_message"), {
        variant: unauthorized ? "error" : "success",
      });
    },
    [enqueueSnackbar, t],
  );

  const logout = useCallback(
    async ({ unauthorized = false }: LogoutParams = {}) => {
      try {
        await appLogout();
      } catch (error) {
        captureException(error);
      } finally {
        removeUser();
        setLogoutNotification(unauthorized);
        queryClient.removeQueries();
      }
    },
    [appLogout, removeUser, setLogoutNotification],
  );

  useEffect(() => {
    reloadUser();
    setIsInitialized(true);
  }, [reloadUser]);

  useEffect(() => {
    if (user?.account_id) {
      pushToDataLayer("accountIdSet", { accountId: user.account_id });
    }
  }, [user?.account_id]);

  const contextValue = useMemo(
    () => ({
      accountDetails,
      user,
      login: mutate,
      isLoading: isPending,
      logout,
      isLogoutPending,
      reloadUser,
      isInitialized,
    }),
    [accountDetails, user, mutate, isPending, logout, isLogoutPending, reloadUser, isInitialized],
  );
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }
  return context;
};

export { AuthProvider, useAuth };
